import {
  PRO50_PRICES,
  PRO50_PRICING_POLICY,
  PRO50_TIER_LABEL,
  isGroup50LegacyHonorPeriod,
  isGroup50Period,
  isPro50Active,
} from './pro50Campaign';

export const GROUP_PLAN_PRICES = {
  '1month': { label: '1개월', fullLabel: '왓퀴즈AI PRO 1개월권', price: 24000, days: 30 },
  '3month': { label: '3개월', fullLabel: '왓퀴즈AI PRO 3개월권', price: 60000, days: 90 },
  '6month': { label: '6개월', fullLabel: '왓퀴즈AI PRO 6개월권', price: 110000, days: 180 },
  '12month': { label: '12개월', fullLabel: '왓퀴즈AI PRO 12개월권', price: 200000, days: 365 },
};

export const GROUP_DISCOUNT_TIERS = [
  { min: 30, label: '30명 이상', discountRate: 0.30 },
  { min: 20, label: '20~29명', discountRate: 0.20 },
  { min: 10, label: '10~19명', discountRate: 0.10 },
  { min: 5, label: '5~9명', discountRate: 0.05 },
  { min: 1, label: '1~4명', discountRate: 0 },
];

export const SECRET_50_GROUP_PRICES = {
  '1month': 24000,
  '3month': 49000,
  '6month': 69000,
  '12month': 99000,
};

export const SECRET_50_PROMO_CODES = new Set([
  'PRIVATE99',
  'EXHIBITION2026',
  'BROCHUREQR2026',
]);

const PROMO_CODE_TO_PUBLIC_SLUG = {
  PRIVATE99: 'private99',
  EXHIBITION2026: 'exhibition2026',
  BROCHUREQR2026: 'brochureqr2026',
};

// ── 10명 이상 단체 50% 이벤트 (/events/group-50, 캠페인 group50_2026q3) ──
// 할인 정의: promo=group50 경로로 들어온 구매에서 인원 10명 이상이면
// 현재 판매가의 50%를 단가로 적용한다 (예: 6개월 110,000원 → 55,000원/인).
// 10명 미만이면 기존 인원별 티어로 폴백한다.
// 서버(backend/services/group_pricing.py)가 같은 규칙으로 금액을 재계산·검증하므로
// 이 값은 표시용이다. 2026-09-30 23:59:59 KST에 자동 종료되고(pro50Campaign.GROUP50_EVENT_END),
// 10/30까지는 9월 견적 보호용으로 10월 이벤트가와 비교해 싼 쪽을 쓴다.
export const GROUP50_EVENT_PROMO_CODE = 'GROUP50';
export const GROUP50_EVENT_SLUG = 'group50';
export const GROUP50_EVENT_MIN_QUANTITY = 10;
export const GROUP50_EVENT_DISCOUNT_RATE = 0.5;
export const GROUP50_EVENT_ENABLED = true;
export const GROUP50_EVENT_TIER_LABEL = '10명 이상 단체 이벤트';

export const normalizeGroupPlanId = (planId) => {
  const normalized = String(planId || '').replace('pro_', '');
  return GROUP_PLAN_PRICES[normalized] ? normalized : '6month';
};

export const normalizeGroupPromoCode = (promoCode) => {
  const normalized = String(promoCode || '').trim().toUpperCase();
  return SECRET_50_PROMO_CODES.has(normalized) ? normalized : '';
};

export const isSecret50GroupPromo = (promoCode) => Boolean(normalizeGroupPromoCode(promoCode));

export const normalizeGroup50PromoCode = (promoCode) => {
  const raw = String(promoCode || '').trim().toUpperCase();
  return raw === GROUP50_EVENT_PROMO_CODE ? GROUP50_EVENT_PROMO_CODE : '';
};

export const isGroup50EventPromo = (promoCode) => Boolean(normalizeGroup50PromoCode(promoCode));

export const getGroupPromoSlug = (promoCode) => {
  if (isGroup50EventPromo(promoCode)) return GROUP50_EVENT_SLUG;
  const normalized = normalizeGroupPromoCode(promoCode);
  return PROMO_CODE_TO_PUBLIC_SLUG[normalized] || '';
};

export const getGroupDiscountTier = (quantity = 1) => {
  const qty = Math.max(1, Number(quantity) || 1);
  return GROUP_DISCOUNT_TIERS.find((tier) => qty >= tier.min) || GROUP_DISCOUNT_TIERS[GROUP_DISCOUNT_TIERS.length - 1];
};

const buildPriceResult = (normalizedPlanId, qty, unitPrice, tierLabel, pricingPolicy, promoCode) => {
  const plan = GROUP_PLAN_PRICES[normalizedPlanId];
  const discountRate = plan.price ? 1 - (unitPrice / plan.price) : 0;
  return {
    planId: normalizedPlanId,
    paymentPlanId: `pro_${normalizedPlanId}`,
    label: plan.label,
    fullLabel: plan.fullLabel,
    days: plan.days,
    baseUnitPrice: plan.price,
    unitPrice,
    quantity: qty,
    totalAmount: unitPrice * qty,
    discountRate,
    discountPercent: Math.round(discountRate * 100),
    tierLabel,
    pricingPolicy,
    promoCode: promoCode || null,
  };
};

// 10월 이벤트(또는 견적 가격 보장) 기간 후보 — 앞에 올수록 동률일 때 우선한다.
const getPro50PeriodCandidates = (normalizedPlanId, qty, promoCode, now, lockedUnitPrice) => {
  const plan = GROUP_PLAN_PRICES[normalizedPlanId];
  const candidates = [];
  if (lockedUnitPrice) {
    candidates.push([Number(lockedUnitPrice), PRO50_TIER_LABEL, PRO50_PRICING_POLICY, null]);
  }
  if (isPro50Active(now)) {
    candidates.push([PRO50_PRICES[normalizedPlanId], PRO50_TIER_LABEL, PRO50_PRICING_POLICY, null]);
  }
  const secret50Code = normalizeGroupPromoCode(promoCode);
  if (secret50Code) {
    candidates.push([SECRET_50_GROUP_PRICES[normalizedPlanId] || plan.price, '전용 혜택', 'secret_50', secret50Code]);
  }
  if (
    isGroup50EventPromo(promoCode)
    && GROUP50_EVENT_ENABLED
    && qty >= GROUP50_EVENT_MIN_QUANTITY
    && isGroup50LegacyHonorPeriod(now)
  ) {
    candidates.push([
      Math.round(plan.price * (1 - GROUP50_EVENT_DISCOUNT_RATE)),
      GROUP50_EVENT_TIER_LABEL,
      'event_group50',
      GROUP50_EVENT_PROMO_CODE,
    ]);
  }
  const tier = getGroupDiscountTier(qty);
  candidates.push([Math.round(plan.price * (1 - tier.discountRate)), tier.label, 'group_tier', null]);
  return candidates;
};

// backend/services/group_pricing.py의 calculate_group_price와 같은 규칙:
// 10월 이벤트 기간(또는 견적 가격 보장)에는 모든 후보 중 가장 싼 단가 하나,
// 그 밖에는 secret_50 → group50 이벤트(9월, 10명 이상) → 인원별 티어.
export const calculateGroupPrice = (
  planId,
  quantity = 1,
  promoCode = '',
  { now = new Date(), lockedUnitPrice = null } = {},
) => {
  const normalizedPlanId = normalizeGroupPlanId(planId);
  const qty = Math.max(1, Number(quantity) || 1);
  const plan = GROUP_PLAN_PRICES[normalizedPlanId];

  if (isPro50Active(now) || lockedUnitPrice) {
    const candidates = getPro50PeriodCandidates(normalizedPlanId, qty, promoCode, now, lockedUnitPrice);
    const best = candidates.reduce((min, item) => (item[0] < min[0] ? item : min));
    return buildPriceResult(normalizedPlanId, qty, ...best);
  }

  const secret50Code = normalizeGroupPromoCode(promoCode);
  const group50Applies = (
    !secret50Code
    && isGroup50EventPromo(promoCode)
    && GROUP50_EVENT_ENABLED
    && isGroup50Period(now)
    && qty >= GROUP50_EVENT_MIN_QUANTITY
  );

  if (secret50Code) {
    return buildPriceResult(
      normalizedPlanId, qty, SECRET_50_GROUP_PRICES[normalizedPlanId] || plan.price,
      '전용 혜택', 'secret_50', secret50Code,
    );
  }
  if (group50Applies) {
    return buildPriceResult(
      normalizedPlanId, qty, Math.round(plan.price * (1 - GROUP50_EVENT_DISCOUNT_RATE)),
      GROUP50_EVENT_TIER_LABEL, 'event_group50', GROUP50_EVENT_PROMO_CODE,
    );
  }
  const tier = getGroupDiscountTier(qty);
  return buildPriceResult(
    normalizedPlanId, qty, Math.round(plan.price * (1 - tier.discountRate)), tier.label, 'group_tier', null,
  );
};

export const getGroupPricingRows = () => (
  GROUP_DISCOUNT_TIERS.map((tier) => ({
    ...tier,
    discountPercent: Math.round(tier.discountRate * 100),
    prices: Object.fromEntries(
      Object.entries(GROUP_PLAN_PRICES).map(([planId, plan]) => [
        planId,
        Math.round(plan.price * (1 - tier.discountRate)),
      ])
    ),
  }))
);
